import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import ListLayout from '../../Generic/ListLayout';
import DataTable from '../../Generic/DataTable';
import confirmDeleteDialog from '../../Generic/confirmDeleteDialog';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../hooks/useDeleteItem';
import useFilterData from '../../../hooks/useFilterData';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { createGeneralColumn, createActiveColumn, createActionColumn, createIsMandatoryColumn } from '../../Generic/dataGridColumns';
import AuthContext from "../../../context/AuthProvider";
import FormSelect from '../../Generic/FormSelect';
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";

const List = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  const { mainStore, criteriaStore } = useStore();
  const { t } = useTranslation();
  const localizedTextsMap = useLocalizedTextsMap();
  useSetMenu(mainStore.CriteriaMenuType, mainStore.CriteriaMenuNo);

  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter states
  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const { register, setValue, formState: { errors } } = useForm({});

  // Load panels on mount
  useEffect(() => {
    const loadPanels = async () => {
      try {
        const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
        setPanels(panelsResponse.data);
      } catch (err) {
        enqueueSnackbar(t("loadDataError"), { variant: "error" });
      }
    };
    loadPanels();
  }, [axiosPrivate, enqueueSnackbar, t]);

  // Fetch criteria whenever any filter changes (or on initial load)
  useEffect(() => {
    const fetchCriteria = async () => {
      try {
        const queryParams = [];
        if (selectedPanel) queryParams.push(`panelId=${selectedPanel.value}`);
        if (selectedCategory) queryParams.push(`categoryId=${selectedCategory.value}`);
        if (selectedSubcategory) queryParams.push(`subcategoryId=${selectedSubcategory.value}`);
        const queryString = queryParams.length ? `?${queryParams.join('&')}` : "";
        const response = await axiosPrivate.get(`/Criterias/Filtered${queryString}`);
        
        // Check if the response is an array. If not, set an empty array.
        if (Array.isArray(response.data)) {
          criteriaStore.setData(response.data);
        } else {
          criteriaStore.setData([]);
        }
      } catch (error) {
        enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
      }
    };
    fetchCriteria();
  }, [
    selectedPanel,
    selectedCategory,
    selectedSubcategory,
    axiosPrivate,
    criteriaStore,
    enqueueSnackbar,
    t,
  ]);

  // Handlers for filter changes with null-check
  const handlePanelChange = async (selectedOption) => {
    if (!selectedOption) {
      // If cleared, reset the panel and all dependent filters
      setValue("panelId", null, { shouldValidate: true });
      setSelectedPanel(null);
      setCategories([]);
      setSubcategories([]);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setValue("categoryId", null, { shouldValidate: true });
      setValue("subcategoryId", null, { shouldValidate: true });
      return;
    }
    setValue("panelId", selectedOption.value, { shouldValidate: true });
    setSelectedPanel(selectedOption);
    try {
      const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
      setCategories(
        typeof response.data === "string" && response.data === "No records" ? [] : response.data
      );
      // Reset dependent selects
      setSubcategories([]);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setValue("categoryId", null, { shouldValidate: true });
      setValue("subcategoryId", null, { shouldValidate: true });
    } catch (error) {
      enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
    }
  };

  const handleCategoryChange = async (selectedOption) => {
    if (!selectedOption) {
      // If cleared, reset the category and its dependent select
      setValue("categoryId", null, { shouldValidate: true });
      setSelectedCategory(null);
      setSubcategories([]);
      setSelectedSubcategory(null);
      setValue("subcategoryId", null, { shouldValidate: true });
      return;
    }
    setValue("categoryId", selectedOption.value, { shouldValidate: true });
    setSelectedCategory(selectedOption);
    try {
      const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
      setSubcategories(
        typeof response.data === "string" && response.data === "No records" ? [] : response.data
      );
      setSelectedSubcategory(null);
      setValue("subcategoryId", null, { shouldValidate: true });
    } catch (error) {
      enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
    }
  };

  const handleSubcategoryChange = (selectedOption) => {
    if (!selectedOption) {
      setValue("subcategoryId", null, { shouldValidate: true });
      setSelectedSubcategory(null);
      return;
    }
    setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
    setSelectedSubcategory(selectedOption);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Delete operation remains unchanged
  const deleteCriteria = useDeleteItem(axiosPrivate, '/Criterias', criteriaStore.deleteCriteria);
  const remove = useCallback((id) => {
    confirmDeleteDialog(t, deleteCriteria, id);
  }, [deleteCriteria, t]);

  // Create columns using useMemo
  const columns = useMemo(() => [
    createGeneralColumn("name", "criteria", t),
    createGeneralColumn("weightCoefficient", "criteriaWeightCoefficient", t, 50),
    createIsMandatoryColumn(t, 100),
    createGeneralColumn("subcategoryName", "subcategory", t),
    createGeneralColumn("categoryName", "category", t),
    createGeneralColumn("panelName", "panel", t),
    createActiveColumn(t),
    createActionColumn(t, "/Criteria/Edit", remove)
  ], [t, remove]);

  const filteredData = useFilterData(toJS(criteriaStore.criteria), searchQuery);

  return (
    <ListLayout
      title={t("criteriaListTitle")}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      searchPlaceholder={t("searchPlaceholder")}
      addButtonLink="/Criteria/Add"
      addButtonTitle={t("add")}
    >
      <div className="row">
        <FormSelect
          label="panel"
          name="panelId"
          register={register}
          errors={errors}
          colSize={3}
          options={panels}
          onChange={handlePanelChange}
          isClearable={true}
        />
        <FormSelect
          label="category"
          name="categoryId"
          value={selectedCategory}
          register={register}
          errors={errors}
          colSize={3}
          options={categories}
          onChange={handleCategoryChange}
          isClearable={true}
        />
        <FormSelect
          label="subcategory"
          name="subcategoryId"
          value={selectedSubcategory}
          register={register}
          errors={errors}
          colSize={3}
          options={subcategories}
          onChange={handleSubcategoryChange}
          isClearable={true}
        />
      </div>
      <DataTable
        rows={filteredData}
        columns={columns}
        pageSize={pageSize}
        setPageSize={setPageSize}
        localeTextsMap={localizedTextsMap}
        // If your data rows use a property different than 'id', specify a custom row ID getter:
        // getRowId={(row) => row.Id} // for example, if each row has an 'Id' property
      />
    </ListLayout>
  );
};

export default observer(List);